/**
 * ★ THE ONE TOKEN SOURCE. Every HTTP client in this app that needs a bearer token gets it from here,
 * and this reads it off the auth client's CURRENT SESSION and nowhere else.
 *
 * WHY THIS FILE EXISTS. Five seams were written, each with a `getAccessToken` / `getIdToken`
 * parameter and each with a version of one sentence under it: "Sign-in is a different surface and
 * this feature must not grow a second Firebase client, so the session owner hands one function in"
 * (`features/calendar/api/http-calendar-api.ts`; the cabinet's own is
 * `features/cabinet/api/http-cabinet-api.ts`). Nothing ever handed one in: the cabinet's
 * seam installed its default with no token source at all, so a build with the live flag set threw
 * at the first screen instead of sending a request. This is the function they were all waiting
 * for, written once.
 *
 * IT DOES NOT HOLD A TOKEN. It asks the session for one on every call, because the Firebase SDK
 * already caches the ID token and refreshes it near expiry, and "a captured one turns into a 401
 * storm at minute sixty-one" (`features/application/api/http.ts`).
 *
 * IT IS THE SAME SESSION THE PROVIDER USES. `AuthSessionProvider` reads the live session object the
 * auth client hands its subscription; `currentSession()` is that client's own answer to the same
 * question (the `AuthClient` port in ../firebase/auth-port.ts), and `authClient()` is memoised so
 * there is only one client to ask. So a screen's request and the provider's `GET /v1/me` carry the
 * token of the same person. (Both are named by symbol rather than by line: that directory is the
 * Firebase adapter's, and it is being rewritten in the same wave as this file.)
 *
 * NO SESSION IS `null`, NOT AN EXCEPTION. That is the contract every seam already declares -
 * "Returning null is a real state - the cold-start visitor who has not signed in"
 * (`features/cabinet/api/http-cabinet-api.ts`) - and the request then goes out bare and is
 * answered 401 `Bearer realm="moonx"`. A token the SDK could not PRODUCE is different, and it is
 * left to throw: that is "cannot verify right now", which the provider holds as `unreachable`
 * rather than as a sign-out (`./AuthSessionProvider.tsx`), and turning it into `null` here
 * would turn a Google outage into a 401 - the exact defect adr/0010-firebase-integration.md rules
 * against.
 *
 * `forceRefresh` IS THE ONE FORCED REFRESH a bare 401 is worth (`AuthSession.getIdToken` in the
 * port). The provider spends it on `GET /v1/me` (`./AuthSessionProvider.tsx`), and the
 * five screen clients spend it through ONE shared rule, `./send-with-session.ts`, which asks this
 * source with `true` at most once per request and otherwise always with `false`.
 *
 * ★ AND A FORCED REFRESH IS SINGLE-FLIGHT, because "once per request" is not "once". A token that
 * has gone stale goes stale for every request at the same moment: a screen that fires four reads
 * gets four bare 401s, and each asked the SDK for its own forced refresh - four round trips to
 * Google for one new token, which the SDK does not share between concurrent callers. So a forced
 * call made while one is already in flight FOR THE SAME PERSON joins it and gets the same token -
 * the one exception to asking on every call, and it holds nothing once the flight has landed: a
 * forced call made after it settles, resolved or rejected, starts a fresh one, so a failed refresh
 * is never handed to the next caller. A call without `forceRefresh` never waits on it. The person
 * is the uid and not the session object, which both clients rebuild on every `currentSession()`;
 * and it must be the person, because a refresh begun for somebody who has since signed out must
 * not hand their token to whoever signed in (`./send-with-session.ts`, "ONE PERSON PER REQUEST").
 */
import { authClient } from '../firebase/create-auth-client';
import type { AuthClient } from '../firebase/auth-port';

/**
 * The shape every seam accepts - the cabinet, editor, calendar and support seams declare the same
 * one-optional-argument signature, so the forced refresh reaches the SDK through all of them; a
 * zero-argument function a test hands in is still assignable, it just cannot be forced. Named apart
 * from the cabinet's own `AccessTokenSource` so the two are never confused in one import list.
 */
export type SessionTokenSource = (forceRefresh?: boolean) => Promise<string | null>;

/**
 * Builds a token source over a client getter. The getter, not the client, so the memoised
 * `authClient()` is asked at CALL time - a module that imports this before the client is first
 * built must not freeze a stale one in. Tests pass a getter over their own fake.
 */
export function createAccessTokenSource(client: () => AuthClient = authClient): SessionTokenSource {
  /** The forced refresh in flight, and whose it is. See the header: it is shared, never kept. */
  let refreshing: { readonly uid: string; readonly token: Promise<string> } | null = null;

  return async (forceRefresh = false) => {
    const session = client().currentSession();
    if (session === null) {
      return null;
    }
    if (!forceRefresh) {
      return session.getIdToken(false);
    }
    const uid = session.identity.uid;
    if (refreshing !== null && refreshing.uid === uid) {
      return refreshing.token;
    }
    const flight = { uid, token: session.getIdToken(true) };
    refreshing = flight;
    // Registered before any caller awaits the token, so the slot is empty by the time one resumes.
    // Both outcomes clear it; the rejection itself still reaches every caller through `token`.
    const settled = () => {
      if (refreshing === flight) {
        refreshing = null;
      }
    };
    flight.token.then(settled, settled);
    return flight.token;
  };
}

/** The app's token source. Every seam defaults to this when nothing is passed in. */
export const accessToken: SessionTokenSource = createAccessTokenSource();

/**
 * WHOSE token the source above would hand out right now - the uid of the current session, or
 * `null`. Read at call time off the same client, for the same reason the token is.
 *
 * It exists for one reader, `./send-with-session.ts`: a request refused before an account switch
 * must neither be replayed with the next person's token nor end the next person's session, and the
 * uid is how the retry and the report tell the two people apart.
 */
export function currentSessionUid(client: () => AuthClient = authClient): string | null {
  return client().currentSession()?.identity.uid ?? null;
}

/**
 * The same source in the application flow's shape, which says "no token" with `undefined`
 * (`features/application/api/http.ts`). An adapter rather than a second source, and it passes
 * `forceRefresh` through: dropping it here would leave the application flow the one screen client
 * whose single forced refresh never reached the SDK.
 */
export function idTokenOrUndefined(
  source: SessionTokenSource = accessToken,
): (forceRefresh?: boolean) => Promise<string | undefined> {
  return async (forceRefresh = false) => (await source(forceRefresh)) ?? undefined;
}
